/**
 * @file     MonthlyEstimateButton.tsx
 * @purpose  "{amount}/mo principal and interest at {rate}" linking to the
 *           monthly costs (#mortgage). Reads the shared ListingMortgageState,
 *           so the hero, the bars and the CostRail always show one figure at
 *           one named rate; its first render uses the same defaults the state
 *           starts with, so the server HTML and hydration agree.
 */
'use client';

import type { MouseEvent } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { listingDetailContent as L } from '@/core/content/listingDetailSharedContent';
import { hubColors } from '@/components/hub/hubKitStyles';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { MORTGAGE_DEFAULTS } from '@/features/mortgage-calculator-flow/types';
import { calculateMortgageBreakdown, formatCAD } from '@/utils/finance/mortgage';
import { useListingMortgageState } from './ListingMortgageState';
import { DETAIL_TAB_MORTGAGE, openDetailTab } from './DetailTabsShell';

/** Monthly principal and interest on the shared state (20% down, 25 years and the posted rate by default). */
export function usePrincipalAndInterest(price: number | null): { amount: string; rate: string } | null {
  const state = useListingMortgageState();
  if (price == null || price <= 0) return null;
  const downPct = state?.downPct ?? 20;
  const rate = state?.rate ?? MORTGAGE_DEFAULTS.interestRate;
  const monthly = calculateMortgageBreakdown({
    homePrice: price,
    downPayment: Math.round(price * (downPct / 100)),
    annualInterestRate: rate,
    amortizationYears: state?.amortization ?? 25,
    annualPropertyTax: 0,
    annualInsurance: 0,
    monthlyHoa: 0,
  }).monthlyPrincipalAndInterest;
  return { amount: formatCAD(monthly), rate: `${rate}%` };
}

const chevron = (
  <Box component="svg" aria-hidden viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" sx={{ flexShrink: 0 }}>
    <path d="M9 18l6-6-6-6" />
  </Box>
);

interface Props {
  price: number;
}

export function MonthlyEstimateButton({ price }: Props) {
  const { t } = useTranslation();
  const pi = usePrincipalAndInterest(price);
  if (!pi) return null;
  const text = t(L.summary.monthly.key, { defaultValue: L.summary.monthly.defaultValue, amount: '\u0000', rate: pi.rate });
  const [before, after] = text.split('\u0000');

  return (
    <Box
      component="a"
      href={`#${DETAIL_TAB_MORTGAGE}`}
      data-monthly-estimate
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        // The MLS body's section nav listens for openDetailTab: it scrolls,
        // moves focus and keeps the funnel event. Elsewhere the anchor jumps.
        if (!document.querySelector('[data-listing-sections]') || !document.getElementById(DETAIL_TAB_MORTGAGE)) return;
        event.preventDefault();
        openDetailTab(DETAIL_TAB_MORTGAGE, 'monthly_estimate');
      }}
      aria-label={t(L.summary.monthlyA11y.key, { defaultValue: L.summary.monthlyA11y.defaultValue, amount: pi.amount, rate: pi.rate })}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 32,
        fontSize: 14,
        lineHeight: 1.4,
        color: hubColors.textSecondary,
        textDecoration: 'none',
        borderRadius: '4px',
        '@media (hover: hover)': { '&:hover': { color: hubColors.textPrimary } },
        '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
      }}
    >
      <span>
        {before}
        <Box component="strong" sx={{ fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
          {pi.amount}
        </Box>
        {after}
      </span>
      {chevron}
    </Box>
  );
}
